import { db } from "../config/firebase";
import {
  collection,
  query,
  orderBy,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  where,
} from "firebase/firestore";
import { COLLECTIONS } from "../constants";

// COLLECTION REFERENCES
export const usersCollection = collection(db, COLLECTIONS.USERS);
export const foldersCollection = collection(db, COLLECTIONS.FOLDERS);
export const bookmarksCollection = collection(db, COLLECTIONS.BOOKMARKS);

// QUERY REFERENCES
export const bookmarksQuery = query(
  bookmarksCollection,
  orderBy("title", "asc")
);

// => READ FUNCTIONS

export const fetchBookmark = async (id: string) => {
  const docRef = await getDoc(doc(db, COLLECTIONS.BOOKMARKS, id));
  return docRef.data();
};

export const getCollection = async (collectionName: string) => {
  const queryRef = query(
    collection(db, collectionName),
    orderBy("title", "asc")
  );

  const querySnapshot = await getDocs(queryRef);
  const data = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
  return data;
};

export const getDocument = async (collectionName: string, id: string) => {
  const docRef = await getDoc(doc(db, collectionName, id));
  return docRef.data();
};

// FETCH BOOKMARKS BY FOLDER ID AND ACCESSIBLE BY LOGGED IN USER
export const fetchBookmarksByFolderId = async (
  folderId: string,
  email: string
) => {
  const queryRef = query(
    bookmarksCollection,
    where("folderId", "==", folderId),
    where("createdBy", "==", email),
    orderBy("title", "asc")
  );

  const querySnapshot = await getDocs(queryRef);
  const data = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
  return data;
};

// FETCH FOLDERS ACCESSIBLE BY LOGGED IN USER
export const fetchFolders = async (email: string) => {
  const queryRef = query(
    foldersCollection,
    where("accessTo", "array-contains", email),
    orderBy("name", "asc")
  );

  const querySnapshot = await getDocs(queryRef);
  const data = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));
  return data;
};

// => CREATE FUNCTIONS

export const createUser = async (data: any) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.USERS), data);
  return docRef.id;
};

// [PENDING] CREATING BOOKMARK NEEDS TO PUSH BOOKMARK TO GIVEN FOLDER IF ANY
export const createBookmark = async (data: any) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.BOOKMARKS), data);
  return docRef.id;
};

export const createFolder = async (data: any) => {
  const docRef = await addDoc(collection(db, COLLECTIONS.FOLDERS), {
    ...data,
    accessTo: [],
  });
  return docRef.id;
};

export const addDocument = async (collectionName: string, data: any) => {
  const docRef = await addDoc(collection(db, collectionName), data);
  return docRef.id;
};

// => UPDATE FUNCTIONS

export const updateBookmark = async (id: string, data: any, email: string) => {
  // CHECK IF THE BOOKMARK IS CREATED BY LOGGED IN USER
  const docRef = await getDoc(doc(db, COLLECTIONS.BOOKMARKS, id));
  if (docRef?.data()?.createdBy === email) {
    await updateDoc(doc(db, COLLECTIONS.BOOKMARKS, id), data);
  } else {
    throw new Error("You are not authorized to update this bookmark");
  }
};

export const updateDocument = async (
  collectionName: string,
  id: string,
  data: any
) => {
  await updateDoc(doc(db, collectionName, id), data);
};

export const updateFolderName = async (id: string, name: string) => {
  await updateDoc(doc(db, COLLECTIONS.FOLDERS, id), { name });
};

// give access to folder to given email
export const shareFolder = async (folderId: string, email: string) => {
  const docRef = await getDoc(doc(db, COLLECTIONS.FOLDERS, folderId));
  // if there is no accessTo field, create it
  if (!docRef?.data()?.accessTo) {
    await updateDoc(doc(db, COLLECTIONS.FOLDERS, folderId), {
      accessTo: [email],
    });
  }
  const accessTo = docRef?.data()?.accessTo;
  if (accessTo.includes(email)) {
    throw new Error("User already has access to this folder");
  } else {
    accessTo.push(email);
    await updateDoc(doc(db, COLLECTIONS.FOLDERS, folderId), { accessTo });
  }
};

// remove access to folder from given email
export const removeAccessToFolder = async (folderId: string, email: string) => {
  const docRef = await getDoc(doc(db, COLLECTIONS.FOLDERS, folderId));
  const accessTo = docRef?.data()?.accessTo;
  if (accessTo.includes(email)) {
    const filteredAccessTo = accessTo.filter((item: string) => item !== email);
    await updateDoc(doc(db, COLLECTIONS.FOLDERS, folderId), {
      accessTo: filteredAccessTo,
    });
  } else {
    throw new Error("User does not have access to this folder");
  }
};

// => DELETE FUNCTIONS

export const deleteBookmark = async (id: string, email: string) => {
  // CHECK IF THE BOOKMARK IS CREATED BY LOGGED IN USER
  const docRef = await getDoc(doc(db, COLLECTIONS.BOOKMARKS, id));
  if (docRef?.data()?.createdBy === email) {
    await deleteDoc(doc(db, COLLECTIONS.BOOKMARKS, id));
  } else {
    throw new Error("You are not authorized to delete this bookmark");
  }
};

// delete folder if it is created by logged in user, and delete all bookmarks in it
export const deleteFolder = async (id: string, email: string) => {
  // CHECK IF THE FOLDER IS CREATED BY LOGGED IN USER
  const docRef = await getDoc(doc(db, COLLECTIONS.FOLDERS, id));
  if (docRef?.data()?.createdBy === email) {
    await deleteDoc(doc(db, COLLECTIONS.FOLDERS, id));
    // delete all bookmarks in this folder
    const bookmarks = await fetchBookmarksByFolderId(id, email);
    bookmarks.forEach(async (bookmark: any) => {
      await deleteBookmark(bookmark.id, email);
    });
  } else {
    throw new Error("You are not authorized to delete this folder");
  }
};

export const deleteDocument = async (collectionName: string, id: string) => {
  await deleteDoc(doc(db, collectionName, id));
};

// => UTIL FUNCTIONS

export const isUserExists = async (email: any) => {
  const query4User = query(
    collection(db, "users"),
    where("email", "==", email)
  );
  const querySnapshot = await getDocs(query4User);
  return !querySnapshot.empty;
};

export const changeCreatedBy = async () => {
  const query4Bookmarks = query(collection(db, COLLECTIONS.BOOKMARKS));
  const querySnapshot = await getDocs(query4Bookmarks);
  querySnapshot.forEach(async (mydoc: any) => {
    const createdBy = mydoc.data().createdBy;
    // if (createdBy.includes("@")) return;
    if (!createdBy) {
      await updateDoc(doc(db, COLLECTIONS.BOOKMARKS, mydoc.id), {
        createdBy: "cmg.dev.projects@gmail.com",
      });
    }
  });
};
